import { getFromCache } from "@app/cache";
import { App } from "@app/context";
import { closeNav } from "@app/nav";
import { PageManager } from "@app/page-manager";

import { Transition } from "@engine/router/transition";

export function setActiveNavLink(url: string): void {
  try {
    const normalized = url !== "/" ? url.replace(/\/$/, "") : "/";
    const nav = document.getElementById("nav");
    const links = document.querySelectorAll<HTMLElement>("#nav .nav-link");
    const mainLinks = document.querySelectorAll<HTMLElement>(".main-link");
    const closeLink = document.querySelectorAll<HTMLElement>(".close-link");

    if (nav) {
      if (normalized === "/" || normalized === "/index") {
        nav.classList.add("is-hidden");
      } else {
        nav.classList.remove("is-hidden");
      }
    }

    if (normalized !== "/" && normalized !== "/index") {
      closeLink.forEach(
        (link) =>
          !link.classList.contains("is-case") && link.classList.add("is-case")
      );
      mainLinks.forEach(
        (link) =>
          !link.classList.contains("is-case") && link.classList.add("is-case")
      );
    } else {
      closeLink.forEach(
        (link) =>
          link.classList.contains("is-case") && link.classList.remove("is-case")
      );
      mainLinks.forEach(
        (link) =>
          link.classList.contains("is-case") && link.classList.remove("is-case")
      );
    }

    links.forEach((link) => {
      const anchor = link.querySelector<HTMLAnchorElement>("a[href]");
      const href = anchor?.getAttribute("href");
      if (
        !href ||
        href.startsWith("http") ||
        href.startsWith("mailto:") ||
        href.startsWith("#")
      ) {
        link.classList.remove("is-active");
        return;
      }
      const linkUrl = href !== "/" ? href.replace(/\/$/, "") : "/";
      if (linkUrl === normalized) link.classList.add("is-active");
      else link.classList.remove("is-active");
    });
  } catch { }
}

export function navigateRoute(path: string): void {
  const pageKey = App.config.routes[path] ?? null;
  const prev = App.route.new;

  App.route.old = { ...prev };
  App.route.new = { url: path, page: pageKey };

  if (prev.page) App.is[prev.page] = false;
  if (pageKey) App.is[pageKey] = true;

  for (const k in App.was) App.was[k] = false;
  if (prev.page) App.was[prev.page] = true;
}

export async function out(
  path: string,
  target: EventTarget | string | null = null
): Promise<void> {
  if (App.mutating) return;
  if (path === App.route.new.url) return;
  App.mutating = true;
  closeNav();

  console.debug("%c● navigate %c" + App.route.new.url + " → " + path, "color:#f97316;font-weight:bold", "color:#f97316");

  navigateRoute(path);
  App.target = target;

  // Pause videos before any page-out logic runs
  const gpu = App.gpu;
  gpu?.setVideosPlaying?.(false);

  // Animate nav out early when going to home so it slides up during the exit
  if (App.route.new.page === 'home') {
    const nav = document.getElementById('nav');
    if (nav) nav.classList.add('is-hidden');
  }

  console.debug("%c  ▸ page.out", "color:#f97316");
  try {
    await PageManager.beforeOut();
  } catch { }

  console.debug("%c  ▸ transition.out", "color:#f97316");
  await Transition.out();
  await _in();
}

async function _in(): Promise<void> {
  const { url } = App.route.new;
  if (!url) {
    App.mutating = false;
    return;
  }
  const entry = getFromCache(url);

  if (!entry) {
    console.error("[router] Missing cache for", url, "— check ak.json.");
    App.mutating = false;
    return;
  }

  if (entry.title) document.title = entry.title;

  const app =
    (App.app as HTMLElement | null) ||
    (document.getElementById("app") as HTMLElement | null);
  if (!app) {
    App.mutating = false;
    return;
  }
  App.app = app;
  app.innerHTML = entry.html || "";

  if (!App.gpu) window.scrollTo(0, 0);

  // Lock native scroll immediately for pages that manage their own scroll
  // Prevents the split-second where native scroll fires before page init
  // Only on desktop (GPU enabled) — mobile uses native scroll
  const newPage = App.route.new.page;
  if ((newPage === "projects" || newPage === "play") && App.gpu) {
    document.documentElement.style.overflow = "hidden";
    app.style.position = "fixed";
  }

  console.debug("%c  ▸ dom.swap %c" + url, "color:#f97316", "color:#f97316;font-weight:normal");

  const container = document.getElementById("page");
  App.container = container;
  try {
    App.scroller?.setActiveRoute?.(url);
  } catch { }

  if (App.target !== "back") {
    history.pushState({ page: url }, "", url);
  }

  try {
    setActiveNavLink(url);
  } catch { }

  console.debug("%c  ▸ transition.in", "color:#f97316");
  await Transition.in();

  console.debug("%c● ready", "color:#22c55e;font-weight:bold");
  App.mutating = false;
}
